/**
 * Plan de tracking PRODUIT — une seule liste blanche pour PostHog et Amplitude.
 *
 * Les noms d'événements du code sont en `snake_case` au format GA4
 * (`add_to_cart`, `begin_checkout`) parce qu'ils sont PARTAGÉS avec Google et
 * Meta, qui imposent ce vocabulaire pour l'attribution — c'est déjà la
 * convention de `./google` et `./facebook`. Amplitude, lui, attend la
 * convention Object-Action :
 *
 *   [Nom] + [Verbe au passé], en Title Case — « Cart Viewed », « Order Completed »
 *
 * Amplitude traite `Song Played`, `song played` et `Played Song` comme TROIS
 * événements distincts : la casse et l'ordre ne sont pas cosmétiques, et aucun
 * des deux outils ne sait fusionner deux noms a posteriori.
 *
 * Les noms sont ALIGNÉS sur ceux de l'app mobile : sans vocabulaire commun,
 * aucun funnel ne peut se lire web + app ensemble.
 *
 * ── Pourquoi une liste blanche ─────────────────────────────────────────────
 *
 * Les deux outils sont facturés à l'ÉVÉNEMENT, à des plafonds différents :
 *   - PostHog **s'ARRÊTE** à 1 M/mois (palier gratuit). Au dépassement on ne
 *     perd pas d'argent, on perd les données du reste du mois — et le quota est
 *     PARTAGÉ avec l'app mobile, qui en consomme déjà ~350 k.
 *   - Amplitude dispose de 2 M et n'en consomme que ~350 k.
 *
 * Google et Meta, eux, ne facturent pas à l'événement et reçoivent TOUT :
 * ils en ont besoin pour l'attribution publicitaire.
 */

/**
 * Le plan. Clé = nom interne (GA4, envoyé tel quel à PostHog), valeur = nom
 * Amplitude.
 */
export const PRODUCT_ANALYTICS_PLAN: Readonly<Record<string, string>> = {
  // ── Acquisition ──────────────────────────────────────────────────────────
  sign_up: "Account Created",
  login: "User Signed In",

  // ── Découverte ───────────────────────────────────────────────────────────
  search: "Search Submitted",
  store_viewed: "Store Viewed",
  home_viewed: "Home Viewed",
  add_to_wishlist: "Product Favorited",

  // ── Panier → commande ────────────────────────────────────────────────────
  add_to_cart: "Product Added",
  view_cart: "Cart Viewed",
  clear_cart: "Cart Cleared",
  begin_checkout: "Checkout Started",
  select_delivery_type: "Delivery Type Selected",
  select_payment_method: "Payment Method Selected",
  add_payment_info: "Payment Info Entered",
  // Nom historique de ce projet, conservé pour ne rien casser côté Meta
  // (`INTERNAL_TO_META` le traduit déjà en `Purchase`).
  order_placed: "Order Placed",

  // ── Échecs : sans eux, un funnel montre la fuite sans jamais l'expliquer ──
  payment_failed: "Payment Failed",
  checkout_failed: "Checkout Failed",

  // ── Promotions ───────────────────────────────────────────────────────────
  apply_promo_code: "Promo Code Applied",
  promo_code_failed: "Promo Code Rejected",
  remove_promo_code: "Promo Code Removed",

  // ── Post-achat & abonnement ──────────────────────────────────────────────
  track_order_clicked: "Order Tracking Opened",
  subscribe: "Subscription Started",
};

/**
 * Sous-ensemble STRICT envoyé à PostHog. Tout le reste du plan ne part qu'à
 * Amplitude, qui a la marge de quota pour l'absorber.
 *
 * PostHog garde le nécessaire pour lire le funnel de bout en bout : entrée,
 * ajout au panier, checkout, paiement, commande, et les deux échecs qui
 * expliquent les fuites. Élargir cette liste est le premier poste de dépense
 * PostHog.
 */
export const POSTHOG_EVENTS: ReadonlySet<string> = new Set([
  "sign_up",
  "login",
  "add_to_cart",
  "begin_checkout",
  "add_payment_info",
  "order_placed",
  "payment_failed",
  "checkout_failed",
]);

/**
 * Événements sciemment ÉCARTÉS du plan, pour qu'on ne les y remette pas par
 * réflexe. Chacun est soit un doublon, soit d'un volume disproportionné à sa
 * valeur analytique. Ils continuent de partir vers Google et Meta.
 */
const DELIBERATELY_EXCLUDED: ReadonlySet<string> = new Set([
  // Doublons du même instant qu'un événement déjà retenu.
  "signup_success",
  "sign_in_success",
  "view_search_results",
  "order_created_success",
  // Navigation produit : plusieurs dizaines par session, et le funnel se lit
  // sur add_to_cart → begin_checkout, pas sur les fiches ouvertes.
  "view_item",
  "view_item_list",
  "view_content",
  "select_item",
  "update_cart_quantity",
  "remove_from_cart",
  // Exemples de la doc / événements purement publicitaires.
  "scroll_feed",
  "ad_impression",
]);

/**
 * `true` si l'événement fait partie du plan produit. Avertit en dev sur un
 * événement inconnu — ni au plan, ni écarté — pour que l'oubli se voie.
 */
export function isInPlan(internalName: string): boolean {
  if (internalName in PRODUCT_ANALYTICS_PLAN) return true;

  if (
    process.env.NODE_ENV !== "production" &&
    !DELIBERATELY_EXCLUDED.has(internalName)
  ) {
    console.warn(
      `[analytics] « ${internalName} » hors plan produit : envoyé ni à PostHog ` +
        "ni à Amplitude (Google et Meta le reçoivent). L'ajouter à " +
        "PRODUCT_ANALYTICS_PLAN ou à DELIBERATELY_EXCLUDED."
    );
  }
  return false;
}

/** Nom Amplitude d'un événement du plan, ou `null` s'il n'en fait pas partie. */
export function toAmplitudeEvent(internalName: string): string | null {
  return PRODUCT_ANALYTICS_PLAN[internalName] ?? null;
}
